import { useState, useCallback, useMemo } from 'react';
import { AsYouType, parsePhoneNumber, CountryCode } from 'libphonenumber-js';

// Dial-code → CountryCode map for the countries supported in our selector
const DIAL_TO_COUNTRY: Record<string, CountryCode> = {
  '+1':   'US',
  '+44':  'GB',
  '+61':  'AU',
  '+91':  'IN',
  '+49':  'DE',
  '+33':  'FR',
  '+34':  'ES',
  '+39':  'IT',
  '+31':  'NL',
  '+55':  'BR',
  '+52':  'MX',
  '+81':  'JP',
  '+82':  'KR',
  '+65':  'SG',
  '+971': 'AE',
  '+27':  'ZA',
  '+63':  'PH',
  '+64':  'NZ',
  '+353': 'IE',
  '+46':  'SE',
  '+47':  'NO',
};

// Trunk-prefix countries: strip leading 0 when dial code is selected
const TRUNK_ZERO_COUNTRIES = new Set(['+44', '+49', '+33', '+34', '+39', '+31', '+61', '+353', '+46', '+47']);

/** Remove tel: prefix and non-dial characters from a pasted string. */
export function cleanPastedNumber(raw: string): string {
  return String(raw ?? '').replace(/^tel:/i, '').replace(/[\s\-()\\/]/g, '');
}

/**
 * Strip everything except digits, *, and # from input.
 * Coerces undefined/null to empty string to avoid `d.replace is not a function`
 * crashes from minified call sites that may pass undefined.
 */
export function cleanDigitsAndSymbols(input: string | undefined | null): string {
  return String(input ?? '').replace(/[^\d*#]/g, '');
}

/** Strip the national trunk 0 for countries that use it (e.g. UK 07911 → 7911). */
export function stripLeadingTrunkZero(number: string, dialCode: string): string {
  if (TRUNK_ZERO_COUNTRIES.has(dialCode) && number.startsWith('0')) {
    return number.slice(1);
  }
  return number;
}

/**
 * If the value starts with +, attempt to parse it and return the matching
 * dial code from our supported list. Returns null if not parseable or unsupported.
 */
export function detectCountryFromE164(value: string): string | null {
  if (!value.startsWith('+')) return null;
  try {
    const parsed = parsePhoneNumber(value);
    if (!parsed?.countryCallingCode) return null;
    const dialCode = `+${parsed.countryCallingCode}`;
    if (dialCode === '+1') {
      return parsed.country === 'CA' ? '+1CA' : '+1';
    }
    return DIAL_TO_COUNTRY[dialCode] ? dialCode : null;
  } catch {
    return null;
  }
}

/**
 * Format a raw local number (no country code prefix) for display using AsYouType.
 * e.g. "7911123456" + "GB" → "7911 123 456"
 */
export function formatAsYouType(localNumber: string, countryCode: CountryCode | undefined): string {
  if (!localNumber) return '';
  if (!countryCode) return localNumber;
  const formatter = new AsYouType(countryCode);
  return formatter.input(localNumber);
}

/**
 * Format what the person typed for an editable input, KEEPING a national
 * trunk 0 ("07911123456" + "+44" → "07911 123456"), so the digits in the
 * field never change under the cursor. formattedDisplay (trunk 0 dropped) is
 * for a "+44 7911 123456" preview next to the country code, not for the input.
 */
export function formatTypedNational(raw: string, dialCode: string): string {
  const dc = dialCode === '+1CA' ? '+1' : dialCode;
  const country = DIAL_TO_COUNTRY[dc];
  if (!raw || !/^\d+$/.test(raw) || !country) return raw;
  const out = formatAsYouType(raw, country);
  return out.replace(/\D/g, '') === raw ? out : raw;
}

interface UsePhoneFormatterOptions {
  /** Current dial code, e.g. "+44". Use "+1" for Canada (+1CA). */
  dialCode: string;
  /** Callback to update the dial code when auto-detected from a pasted number */
  onDialCodeChange: (dialCode: string) => void;
}

type RawNumberUpdater = string | ((prev: string) => string);

interface UsePhoneFormatterReturn {
  rawNumber: string;
  formattedDisplay: string;
  setRawNumber: (value: RawNumberUpdater) => void;
  handleInput: (value: string) => void;
  handlePaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;
}

export function usePhoneFormatter({
  dialCode,
  onDialCodeChange,
}: UsePhoneFormatterOptions): UsePhoneFormatterReturn {
  const [rawNumber, setRawNumberState] = useState('');

  // Normalize +1CA → +1 for formatting purposes
  const normalizedDialCode = dialCode === '+1CA' ? '+1' : dialCode;
  const country = DIAL_TO_COUNTRY[normalizedDialCode] as CountryCode | undefined;
  const formattedDisplay = useMemo(
    () => formatAsYouType(stripLeadingTrunkZero(rawNumber, normalizedDialCode), country),
    [rawNumber, normalizedDialCode, country],
  );

  const handleInput = useCallback(
    (value: string) => {
      // Strip everything except digits, *, # (no + allowed in keyboard input)
      const cleaned = cleanDigitsAndSymbols(value);
      setRawNumberState(cleaned);
    },
    [],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pasted = cleanPastedNumber(e.clipboardData.getData('text'));
      const detected = detectCountryFromE164(pasted);
      if (detected) {
        // Full E.164 number pasted — auto-switch country, store local part
        onDialCodeChange(detected);
        const effectiveDialCode = detected === '+1CA' ? '+1' : detected;
        const localPart = pasted.slice(effectiveDialCode.length);
        setRawNumberState(localPart);
      } else {
        // Local number — clean digits and strip trunk zero
        const nd = dialCode === '+1CA' ? '+1' : dialCode;
        const stripped = stripLeadingTrunkZero(cleanDigitsAndSymbols(pasted), nd);
        setRawNumberState(stripped);
      }
    },
    [dialCode, onDialCodeChange],
  );

  const setRawNumber = useCallback((value: RawNumberUpdater) => {
    if (typeof value === 'function') {
      setRawNumberState(prev => cleanDigitsAndSymbols(value(prev)));
    } else {
      setRawNumberState(cleanDigitsAndSymbols(value));
    }
  }, []);

  return { rawNumber, formattedDisplay, setRawNumber, handleInput, handlePaste };
}
